// Word (and some Google Docs / web) content uses TABLES purely for visual column
// layout — a résumé with side-by-side panels, a "label | value" grid, often with
// an empty spacer cell for the gutter. After HTML deserialization such a table
// arrives as a Plate `table` node; rendered as-is it's a bordered data grid, and
// the empty spacer surfaces as a phantom "extra column" that mangles the imported
// or pasted layout.
//
// convertLayoutTablesToColumns walks the deserialized FRAGMENT and turns a
// single-row table into a Plate column group (`column_group` → one `column` per
// non-empty cell), so the side-by-side layout is PRESERVED and stays editable
// (resizable columns). Genuine data tables (multiple rows) are left untouched, so
// real tables still come in as tables. Widths are distributed evenly; the
// @platejs/layout normalizer assigns column IDs and keeps widths summing to 100%.
//
// Shared by the .docx import path and the HTML paste path so both behave the same.

const T = {
    table: 'table',
    tr: 'tr',
    td: 'td',
    th: 'th',
    columnGroup: 'column_group',
    column: 'column',
    p: 'p',
};

function textOf(node: any): string {
    if (node == null) return '';
    if (typeof node.text === 'string') return node.text;
    if (Array.isArray(node.children)) return node.children.map(textOf).join('');
    return '';
}

function hasImage(node: any): boolean {
    if (node?.type === 'image') return true;
    return Array.isArray(node?.children) && node.children.some(hasImage);
}

function isEmptyCell(cell: any): boolean {
    return textOf(cell).trim().length === 0 && !hasImage(cell);
}

const emptyParagraph = () => ({ type: T.p, children: [{ text: '' }] });
const isInline = (n: any) => !n || typeof n.text === 'string' || !n.type;

// A column's children must be element blocks. Recurse first (a nested layout
// table inside the cell also becomes columns), then wrap any stray inline runs in
// a paragraph so no text is lost.
//
// A nested layout table inside the cell converts to a column_group node. A
// column_group nested directly inside a column is invalid (taxonomy #9:
// column_group nested in a column). Flatten it: hoist the nested column_group's
// column children up as siblings in the current column's block list.
function cellBlocks(cell: any): any[] {
    const converted = convertLayoutTablesToColumns(Array.isArray(cell.children) ? cell.children : []);
    const blocks: any[] = [];
    let inlineRun: any[] = [];
    const flush = () => {
        if (inlineRun.length) {
            blocks.push({ type: T.p, children: inlineRun });
            inlineRun = [];
        }
    };
    for (const n of converted) {
        // A column_group produced by a nested layout table must not become a
        // direct child of a column (invalid nesting: column > column_group).
        // Flatten it: emit its block content as sibling blocks in this column.
        if (n?.type === T.columnGroup && Array.isArray(n.children)) {
            flush();
            for (const col of n.children) {
                if (col?.type === T.column && Array.isArray(col.children)) {
                    for (const block of col.children) {
                        if (isInline(block)) inlineRun.push(block);
                        else {
                            flush();
                            blocks.push(block);
                        }
                    }
                    flush();
                }
            }
            continue;
        }
        if (isInline(n)) inlineRun.push(n);
        else {
            flush();
            blocks.push(n);
        }
    }
    flush();
    return blocks.length ? blocks : [emptyParagraph()];
}

// → column_group node, OR an array of blocks (single content cell), OR null when
// the table is not a single-row layout table (leave it as a data table).
function tableToColumns(table: any): any | any[] | null {
    const rows = (Array.isArray(table.children) ? table.children : []).filter((r: any) => r?.type === T.tr);
    if (rows.length !== 1) return null;
    const cells = (Array.isArray(rows[0].children) ? rows[0].children : []).filter((c: any) => c?.type === T.td || c?.type === T.th);
    if (cells.length < 2) return null;

    const nonEmpty = cells.filter((c: any) => !isEmptyCell(c));
    if (nonEmpty.length === 0) return null;
    if (nonEmpty.length === 1) return cellBlocks(nonEmpty[0]); // one content cell → just inline its blocks

    const width = `${(100 / nonEmpty.length).toFixed(2)}%`;
    return {
        type: T.columnGroup,
        children: nonEmpty.map((c: any) => ({ type: T.column, width, children: cellBlocks(c) })),
    };
}

function recurseInto(node: any): any {
    if (node && Array.isArray(node.children) && typeof node.text !== 'string') {
        return { ...node, children: convertLayoutTablesToColumns(node.children) };
    }
    return node;
}

export function convertLayoutTablesToColumns(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) return nodes;
    const out: any[] = [];
    for (const node of nodes) {
        if (node?.type === T.table) {
            const converted = tableToColumns(node);
            if (converted == null) out.push(recurseInto(node));
            else if (Array.isArray(converted)) out.push(...converted);
            else out.push(converted);
            continue;
        }
        out.push(recurseInto(node));
    }
    return out;
}
